import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AboutMobile from './AboutMobile';
import SAVStamp from './SAVStamp';

interface AboutProps {
  t: any;
  isRTL: boolean;
}

const AboutSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="hex" width="20" height="20" patternUnits="userSpaceOnUse"><polygon points="10,2 18,6 18,14 10,18 2,14 2,6" fill="none" stroke="%23dc2626" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23hex)"/></svg>');
    animation: rotate 30s linear infinite;
    z-index: 1;
  }
  
  @keyframes rotate {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;
`;

const FloatingShape = styled.div`
  position: absolute;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
  border-radius: 50%;
  
  &:nth-child(1) {
    width: 300px;
    height: 300px;
    top: 10%;
    left: 5%;
    animation: float 6s ease-in-out infinite;
  }
  
  &:nth-child(2) {
    width: 200px;
    height: 200px;
    top: 60%;
    right: 10%;
    animation: float 8s ease-in-out infinite reverse;
  }
  
  &:nth-child(3) {
    width: 150px;
    height: 150px;
    bottom: 20%;
    left: 15%;
    animation: float 7s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  width: 100%;
  position: relative;
  z-index: 2;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  display: flex;
  min-height: 100vh;
  padding-top: 2rem;
`;

const SideMenu = styled.div<{ $isRTL: boolean }>`
  width: 280px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(220, 38, 38, 0.1);
  padding: 2rem 0;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #dc2626;
    border-radius: 2px;
  }
  
  @media (max-width: 1024px) {
    width: 240px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    position: relative;
    border-right: none;
    border-bottom: 1px solid rgba(220, 38, 38, 0.1);
    padding: 1rem 0;
  }
`;

const MenuItems = styled.div`
  padding: 0 1.5rem;
`;

const MenuItem = styled.button<{ $isActive: boolean; $isRTL: boolean }>`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: none;
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'transparent'};
  color: ${props => props.$isActive ? '#dc2626' : '#64748b'};
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  border-radius: 12px;
  margin-bottom: 0.5rem;
  position: relative;
  border: 1px solid ${props => props.$isActive ? 'rgba(220, 38, 38, 0.3)' : 'transparent'};
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.3);
    transform: translateX(${props => props.$isRTL ? '-4px' : '4px'});
  }
  
  &::before {
    content: '';
    position: absolute;
    ${props => props.$isRTL ? 'right: 0;' : 'left: 0;'}
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0;
    background: #dc2626;
    border-radius: 2px;
    transition: height 0.3s ease;
  }
  
  ${props => props.$isActive && `
    &::before {
      height: 60%;
    },
          </CardText>
        </div>
      )
    }
  `}
  
  @media (max-width: 768px) {
    padding: 1rem 1.2rem;
    font-size: 0.9rem;
    margin-bottom: 0.3rem;
    
    &:hover {
      transform: none;
    }
  }
`;

const MainContent = styled.div<{ $isRTL: boolean }>`
  flex: 1;
  padding: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  overflow-y: auto;
  height: 100vh;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(241, 245, 249, 0.5);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #dc2626;
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    height: auto;
  }
`;

const ContentArea = styled.div<{ $isRTL: boolean }>`
  max-width: 1000px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3rem;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  background: linear-gradient(135deg, #000000 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #64748b;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  text-align: center;
`;

const ContentCard = styled.div<{ $isRTL: boolean }>`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const CardTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  color: #0f172a;
  margin-bottom: 1.5rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CardText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #64748b;
  margin-bottom: 1.5rem;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ValueCard = styled.div<{ $isRTL: boolean }>`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ValueTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #0f172a;
  margin-bottom: 1rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ValueDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;


const AdvantagesList = styled.ul<{ $isRTL: boolean }>`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const AdvantageItem = styled.li<{ $isRTL: boolean }>`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  gap: 1rem;
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 4px;
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '❖';
    color: white;
    font-size: 0.8rem;
    font-weight: bold;
  }
`;

const AdvantageText = styled.span<{ $isRTL: boolean }>`
  color: #0f172a;
  font-size: 1.05rem;
  line-height: 1.7;
  flex: 1;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

// New styled components for reworked advantages section
const AdvantagesContainer = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
`;

const AdvantagesHeader = styled.div<{ $isRTL: boolean }>`
  text-align: center;
  margin-bottom: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const AdvantagesTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const AdvantagesSubtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const AdvantagesGrid = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const AdvantageCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-left: 4px solid #22c55e;
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const AdvantageCardTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const AdvantageCardText = styled.p<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const StampWrapper = styled.div<{ $isRTL: boolean }>`
  position: absolute;
  top: -0.5rem;
  ${props => props.$isRTL ? 'left: 1rem;' : 'right: 1rem;'}
  z-index: 1000;
  pointer-events: none;
`;

const AboutMobileWrapper = styled.div`
  @media (min-width: 769px) {
    display: none;
  }
`;

const About: React.FC<AboutProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeSection, setActiveSection] = useState(0);

  const menuItems = [
    { id: 'qui-sommes-nous', title: t.aboutTabs.whoWeAre },
    { id: 'historique', title: t.aboutTabs.history },
    { id: 'positioning', title: t.aboutTabs.positioning },
    { id: 'values', title: t.aboutTabs.values },
    { id: 'legend', title: t.aboutTabs.productLegend },
    { id: 'marches-cles', title: t.aboutTabs.keyMarkets },
    { id: 'avantages', title: t.aboutTabs.advantages },
    { id: 'mission', title: t.aboutTabs.mission },
    { id: 'localisation', title: t.aboutTabs.location }
  ];

  const sections = [
    {
      id: 'qui-sommes-nous',
      title: t.aboutContent.whoWeAre.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            <HighlightText>{t.techyid}, {t.semiFiliale}</HighlightText>, {t.aboutContent.whoWeAre.content1.replace(/^TECHYID, (Semi-filiale du groupe AON|Semi-subsidiary of the AON), /, '')}
          </CardText>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content2}
          </CardText>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content3}
          </CardText>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content4}
          </CardText>
        </div>
      )
    },
    {
      id: 'historique',
      title: t.aboutContent.history.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.history.content1}
          </CardText>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.history.content2}
          </CardText>
        </div>
      )
    },
    {
      id: 'positioning',
      title: t.aboutContent.positioning.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.positioning.content1}
          </CardText>
        </div>
      )
    },
    {
      id: 'values',
      title: t.aboutContent.values.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.values.content1}
          </CardText>
        </div>
      )
    },
    {
      id: 'legend',
      title: t.aboutContent.productLegend.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            <HighlightText>{t.techyid}</HighlightText> {t.aboutContent.productLegend.intro}
          </CardText>
          
          <div style={{ marginTop: '2rem' }}>
            <CardText $isRTL={isRTL} style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.2rem' }}>
              {t.aboutContent.productLegend.electricity.title}
          </CardText>
            <CardText $isRTL={isRTL} style={{ marginBottom: '1rem' }}>
              {t.aboutContent.productLegend.electricity.subtitle}
          </CardText>
            <AdvantagesList $isRTL={isRTL}>
              {t.aboutContent.productLegend.electricity.items.map((item, index) => (
                <AdvantageItem key={index} $isRTL={isRTL}>
                <CheckIcon />
                  <AdvantageText $isRTL={isRTL}>{item}</AdvantageText>
              </AdvantageItem>
              ))}
            </AdvantagesList>

            <CardText $isRTL={isRTL} style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem', marginTop: '2rem', fontSize: '1.2rem' }}>
              {t.aboutContent.productLegend.industry.title}
          </CardText>
            <CardText $isRTL={isRTL} style={{ marginBottom: '1rem' }}>
              {t.aboutContent.productLegend.industry.subtitle}
          </CardText>
            <AdvantagesList $isRTL={isRTL}>
              {t.aboutContent.productLegend.industry.items.map((item, index) => (
                <AdvantageItem key={index} $isRTL={isRTL}>
                <CheckIcon />
                  <AdvantageText $isRTL={isRTL}>{item}</AdvantageText>
              </AdvantageItem>
              ))}
            </AdvantagesList>

            <CardText $isRTL={isRTL} style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem', marginTop: '2rem', fontSize: '1.2rem' }}>
              {t.aboutContent.productLegend.building.title}
            </CardText>
            <CardText $isRTL={isRTL} style={{ marginBottom: '1rem' }}>
              {t.aboutContent.productLegend.building.subtitle}
            </CardText>
            <AdvantagesList $isRTL={isRTL}>
              {t.aboutContent.productLegend.building.items.map((item, index) => (
                <AdvantageItem key={index} $isRTL={isRTL}>
                <CheckIcon />
                  <AdvantageText $isRTL={isRTL}>{item}</AdvantageText>
              </AdvantageItem>
              ))}
            </AdvantagesList>

            <CardText $isRTL={isRTL} style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem', marginTop: '2rem', fontSize: '1.2rem' }}>
              {t.aboutContent.productLegend.infrastructure.title}
            </CardText>
            <CardText $isRTL={isRTL} style={{ marginBottom: '1rem' }}>
              {t.aboutContent.productLegend.infrastructure.subtitle}
            </CardText>
            <AdvantagesList $isRTL={isRTL}>
              {t.aboutContent.productLegend.infrastructure.items.map((item, index) => (
                <AdvantageItem key={index} $isRTL={isRTL}>
                <CheckIcon />
                  <AdvantageText $isRTL={isRTL}>{item}</AdvantageText>
              </AdvantageItem>
              ))}
            </AdvantagesList>
          </div>
        </div>
      )
    },
    {
      id: 'marches-cles',
      title: t.aboutContent.keyMarkets.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.building.title}</strong><br/>
            {t.aboutContent.keyMarkets.building.content}
          </CardText>
          <CardText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.industry.title}</strong><br/>
            {t.aboutContent.keyMarkets.industry.content}
          </CardText>
          <CardText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.infrastructure.title}</strong><br/>
            {t.aboutContent.keyMarkets.infrastructure.content}
          </CardText>
          <CardText $isRTL={isRTL}>
            <strong>{t.techyid}</strong>, {t.aboutContent.keyMarkets.conclusion}
          </CardText>
        </div>
      )
    },
    {
      id: 'avantages',
      title: t.aboutContent.advantages.title,
      content: (
        <AdvantagesContainer $isRTL={isRTL}>
          <StampWrapper $isRTL={isRTL}>
            <SAVStamp size={180} />
          </StampWrapper>
          
          <AdvantagesHeader $isRTL={isRTL}>
            <AdvantagesSubtitle $isRTL={isRTL}>
              {t.aboutContent.advantages.subtitle}
            </AdvantagesSubtitle>
          </AdvantagesHeader>

          <AdvantagesGrid $isRTL={isRTL}>
            {t.aboutContent.advantages.items.map((item, index) => (
              <AdvantageCard key={index} $isRTL={isRTL}>
              <AdvantageCardTitle $isRTL={isRTL}>
                  {item.title}
              </AdvantageCardTitle>
              <AdvantageCardText $isRTL={isRTL}>
                  {item.content}
              </AdvantageCardText>
            </AdvantageCard>
            ))}
          </AdvantagesGrid>
        </AdvantagesContainer>
      )
    },
    {
      id: 'mission',
      title: t.aboutContent.mission.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            {t.aboutContent.mission.content1}
          </CardText>
          <CardText $isRTL={isRTL}>
            <strong>{t.aboutContent.mission.valuesTitle}</strong>
          </CardText>
          <AdvantagesList $isRTL={isRTL}>
            {t.aboutContent.mission.values.map((value, index) => (
              <AdvantageItem key={index} $isRTL={isRTL}>
              <CheckIcon />
                <AdvantageText $isRTL={isRTL}><strong>{value.title}</strong> : {value.content}</AdvantageText>
            </AdvantageItem>
            ))}
          </AdvantagesList>
        </div>
      )
    },
    {
      id: 'localisation',
      title: t.aboutContent.location.title,
      content: (
        <div>
          <CardText $isRTL={isRTL}>
            <strong>{t.techyid}</strong> {t.aboutContent.location.content1}
          </CardText>
          <div style={{ marginTop: '2rem', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3791.9513070339995!2d-16.0008326!3d18.1200397!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xe964dc2ccbd936d%3A0x2754540aab4bf46a!2sTechyid%20Mauritanie!5e0!3m2!1sen!2s!4v1759489683068!5m2!1sen!2s"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation TECHYID - Tevragh Zeina, Cité plage, lot 41, Nouakchott"
            ></iframe>
          </div>
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <a 
              href="https://maps.app.goo.gl/496c4m5JTW76c3ie8" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#dc2626', 
                textDecoration: 'none', 
                fontWeight: '600',
                padding: '0.75rem 1.5rem',
                border: '2px solid #dc2626',
                borderRadius: '8px',
                display: 'inline-block',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = '#dc2626';
                (e.target as HTMLAnchorElement).style.color = 'white';
              }}
              onMouseOut={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = 'transparent';
                (e.target as HTMLAnchorElement).style.color = '#dc2626';
              }}
            >
              {t.aboutContent.location.mapButton}
            </a>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      {/* Desktop version - hidden on mobile */}
    <AboutSection id="about" ref={ref}>
      <FloatingElements>
        <FloatingShape />
        <FloatingShape />
        <FloatingShape />
      </FloatingElements>

      <Container $isRTL={isRTL}>
        <SideMenu $isRTL={isRTL}>
          <MenuItems>
            {menuItems.map((item, index) => (
              <MenuItem
                key={item.id}
                $isActive={activeSection === index}
            $isRTL={isRTL}
                onClick={() => setActiveSection(index)}
              >
                {item.title}
              </MenuItem>
            ))}
          </MenuItems>
        </SideMenu>

        <MainContent $isRTL={isRTL}>
          <ContentArea $isRTL={isRTL}>
            <SectionTitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t.aboutTitle}
            </SectionTitle>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isRTL ? 30 : -30 }}
                transition={{ duration: 0.5 }}
              >
                <ContentCard $isRTL={isRTL}>
                  <CardTitle $isRTL={isRTL}>
                    {sections[Math.min(activeSection, sections.length - 1)]?.title || 'Loading...'}
                  </CardTitle>
                  {sections[Math.min(activeSection, sections.length - 1)]?.content || <div>Loading...</div>}
                </ContentCard>
              </motion.div>
            </AnimatePresence>
          </ContentArea>
        </MainContent>
      </Container>
    </AboutSection>

      {/* Mobile version - only visible on mobile */}
      <AboutMobileWrapper>
        <AboutMobile t={t} isRTL={isRTL} />
      </AboutMobileWrapper>
    </>
  );
};

export default About;
